import type { AnyFormApi } from "@tanstack/react-form";
import { Ref, useImperativeHandle } from "react";

/**
 * What a parent may do to a form without knowing the form library. The form is typed `AnyFormApi`, so
 * `name` and `value` are checked against `TValues` only through this interface, not by the form itself.
 */
export interface FormHandle<TValues extends object> {
  /** Runs validation and, when valid, the form's `onSubmit`; resolves when it settles, rejects if `onSubmit` throws. */
  submit: () => Promise<void>;
  /**
   * Sets a top-level field and marks it touched and dirty. The change listener fires only when the field
   * is mounted; for an unmounted name the value changes silently.
   */
  setValue: <K extends keyof TValues & string>(name: K, value: TValues[K]) => void;
  /** Current values, read at call time. */
  getValues: () => TValues;
}

/** Exposes a `FormHandle` through the `handleRef` prop a form component accepts. */
export const useFormHandle = <TValues extends object>(form: AnyFormApi, handleRef?: Ref<FormHandle<TValues>>) => {
  useImperativeHandle(
    handleRef,
    () => ({
      submit: () => form.handleSubmit(),
      setValue: (name, value) => form.setFieldValue(name, value),
      getValues: () => form.state.values,
    }),
    [form],
  );
};
